import { zodResolver } from '@hookform/resolvers/zod';
import { useConfirmResetPassword, useResetPassword, useSignIn } from 'korint-sdk/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Alert } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Form } from '../../forms/form';
import { FormPasswordInput } from '../../forms/form-password-input';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../forms/input-otp';
import { Label } from '../../forms/label';
import { buildZodPassword } from './validatePassword';
import { LightbulbIcon } from 'lucide-react';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';

type ResetErrorCode = 'invalidCode' | 'attemptLimitExceeded' | 'codeExpired' | 'rateLimited';

const CONFIRM_RESET_ERROR_MAP = new Map<string, ResetErrorCode>([
  ['Invalid verification code', 'invalidCode'],
  ['Code mismatch', 'invalidCode'],
  ['Attempt limit exceeded', 'attemptLimitExceeded'],
  ['expired', 'codeExpired'],
]);

const RESET_ERROR_MAP = new Map<string, ResetErrorCode>([
  ['Limit exceeded', 'rateLimited'],
  ['Attempt limit exceeded', 'rateLimited'],
]);

// ponytail: actually 4 sends total — the initial auto-send isn't counted, only manual resends
const MAX_RESENDS = 3;

const matchResetErrorCode = (
  errorMessage: string,
  errorMap: Map<string, ResetErrorCode>,
): ResetErrorCode | null => {
  for (const [pattern, code] of errorMap.entries()) {
    if (errorMessage.includes(pattern)) {
      return code;
    }
  }
  return null;
};

export const PasswordSetupForm = ({
  email,
  code,
  onSuccess,
}: {
  email: string;
  code?: string | null;
  onSuccess: () => void;
}) => {
  const { t } = useTranslation();
  const [resetError, setResetError] = useState<string | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const sentStorageKey = `pwsetup-sent:${email}`;
  const resendsStorageKey = `pwsetup-resends:${email}`;
  const [resendsUsed, setResendsUsed] = useState(() =>
    Number(sessionStorage.getItem(resendsStorageKey) ?? 0),
  );
  const resendCapReached = resendsUsed >= MAX_RESENDS;

  const { signIn } = useSignIn();

  const { resetPassword, isLoading: isResetting } = useResetPassword({
    onSuccess: () => {
      setEmailSent(true);
      setCooldown(60);
    },
    onError: (err: any) => {
      const errorCode = matchResetErrorCode(err.message, RESET_ERROR_MAP);
      setResetError(
        errorCode
          ? t(`authPasswordSetup.resetErrors.${errorCode}`)
          : t('authPasswordSetup.resetErrors.generic'),
      );
    },
  });

  const formSchema = useMemo(
    () =>
      z
        .object({
          code: z.string().min(6, t('authPasswordSetup.validation.codeRequired')),
          newPassword: buildZodPassword(t),
          newPasswordConfirmation: z.string(),
        })
        .superRefine((data, ctx) => {
          if (data.newPassword !== data.newPasswordConfirmation) {
            ctx.addIssue({
              code: 'custom',
              path: ['newPasswordConfirmation'],
              message: t('authPasswordSetup.validation.passwordsDoNotMatch'),
            });
          }
        }),
    [t],
  );

  const form = useForm({
    defaultValues: {
      code: code ?? '',
      newPassword: '',
      newPasswordConfirmation: '',
    },
    resolver: zodResolver(formSchema),
  });

  const { confirmResetPassword, isLoading: isConfirming } = useConfirmResetPassword({
    onSuccess: async () => {
      try {
        await signIn({ username: email, password: form.getValues().newPassword });
        onSuccess();
      } catch {
        onSuccess();
      }
    },
    onError: (err: any) => {
      const errorCode = matchResetErrorCode(err.message, CONFIRM_RESET_ERROR_MAP);
      setConfirmError(
        errorCode
          ? t(`authPasswordSetup.resetErrors.${errorCode}`)
          : t('authPasswordSetup.resetErrors.generic'),
      );
    },
  });

  const sendCode = useCallback(() => {
    setResetError(null);
    resetPassword({ username: email });
  }, [resetPassword, email]);

  // ponytail: sessionStorage flag, not a backend send-log — survives reload, clears on tab close; stops a refresh from re-mailing a (possibly dead) address
  useEffect(() => {
    if (sessionStorage.getItem(sentStorageKey)) return;
    sessionStorage.setItem(sentStorageKey, '1');
    sendCode();
  }, [sendCode, sentStorageKey]);

  const handleResend = () => {
    if (cooldown > 0 || resendCapReached) return;
    const next = resendsUsed + 1;
    sessionStorage.setItem(resendsStorageKey, String(next));
    setResendsUsed(next);
    sendCode();
  };

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown(c => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleSubmit = form.handleSubmit(async ({ code, newPassword }) => {
    setConfirmError(null);
    await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
  });

  return (
    <Stack>
      {resetError && <Alert variant="destructive">{resetError}</Alert>}
      {confirmError && <Alert variant="destructive">{confirmError}</Alert>}
      {emailSent && (
        <Alert className="mb-4" variant="info">
          <LightbulbIcon />
          <Text inline size="sm">
            <Trans
              i18nKey="authPasswordSetup.emailSentHint"
              values={{ email }}
              components={[<strong key="email" />]}
            />
          </Text>
        </Alert>
      )}
      {resendCapReached && (
        <Alert variant="info">
          <Text inline size="sm">
            {t('authPasswordSetup.resendCapReached')}
          </Text>
        </Alert>
      )}
      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={handleResend}
        loading={isResetting}
        disabled={cooldown > 0 || resendCapReached}
      >
        {cooldown > 0
          ? t('authPasswordSetup.resendCooldown', { cooldown })
          : t('authPasswordSetup.resendButton')}
      </Button>
      <Form {...form}>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <Stack gap={2}>
            <Label htmlFor="password-setup-otp">{t('authPasswordSetup.codeLabel')}</Label>
            <InputOTP
              id="password-setup-otp"
              aria-label={t('authPasswordSetup.codeLabel')}
              maxLength={6}
              pattern="^[0-9]+$"
              value={form.watch('code')}
              onChange={v => form.setValue('code', v, { shouldValidate: true })}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </Stack>
          <FormPasswordInput
            name="newPassword"
            label={t('authPasswordSetup.newPasswordLabel')}
            placeholder={t('authPasswordSetup.newPasswordPlaceholder')}
            required
            control={form.control}
          />
          <FormPasswordInput
            name="newPasswordConfirmation"
            label={t('authPasswordSetup.confirmPasswordLabel')}
            placeholder={t('authPasswordSetup.confirmPasswordPlaceholder')}
            required
            control={form.control}
          />
          <Button className="w-full" type="submit" loading={isConfirming}>
            {t('authPasswordSetup.submitButton')}
          </Button>
        </form>
      </Form>
    </Stack>
  );
};
